import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { useHistory } from "react-router-dom";
import { ShoppingCart as ShoppingCartIcon } from "@material-ui/icons";
import CircularProgress from "@material-ui/core/CircularProgress";

import { getProductsRequest, selectProduct } from "./store/actions";
import { applyAuthToken } from "../../utils/authToken";
import ProductCard from "../../components/ProductCard";
import { useStyles } from "./styles";
import { getProductImageBackground, getProductThumbnail } from "./productImages";

const getBillingLabel = (product) => {
  if (product.value === 3 || /3_MONTH/i.test(product.name)) {
    return "Quarter";
  }

  if (product.value === 12 || /12_MONTH|1_YEAR/i.test(product.name)) {
    return "Year";
  }

  return "Month";
};

const getProductBaseTitle = (title) =>
  title
    .replace(/\s*-\s*(1\s*Month|3\s*Months|12\s*Months|1\s*Year)$/i, "")
    .replace(/\s+(1\s*Month|3\s*Months|12\s*Months)$/i, "");

const getProductGroupKey = (product) =>
  product.name
    .replace(/_(1|3|12)_MONTHS?$/i, "")
    .replace(/_1_YEAR$/i, "");

const groupProducts = (products) => {
  const billingOrder = {
    Month: 1,
    Quarter: 2,
    Year: 3,
  };

  const productGroups = products.reduce((groups, product) => {
    const key = getProductGroupKey(product);
    const billingLabel = getBillingLabel(product);
    const existingGroup = groups[key];

    const option = {
      ...product,
      billingLabel,
      imageBackground: getProductImageBackground(product),
      thumbnailPath: getProductThumbnail(product),
    };

    if (!existingGroup) {
      groups[key] = {
        ...product,
        imageBackground: getProductImageBackground(product),
        title: getProductBaseTitle(product.title),
        thumbnailPath: getProductThumbnail(product),
        options: [option],
      };
      return groups;
    }

    existingGroup.options.push(option);
    return groups;
  }, {});

  return Object.values(productGroups).map((group) => ({
    ...group,
    options: group.options.sort(
      (current, next) =>
        billingOrder[current.billingLabel] - billingOrder[next.billingLabel]
    ),
  }));
};

export const Purchase = (props) => {
  const { products, isLoading, getProducts, selectProductAction } = props;
  const classes = useStyles();
  const history = useHistory();
  const productGroups = useMemo(() => groupProducts(products), [products]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");

    if (token) {
      const { ok } = applyAuthToken(token);

      // Invalid or expired token: applyAuthToken has already wiped cookie,
      // axios header and profile — land the user on a clean login page.
      if (!ok) {
        history.replace("/pilot/login", { from: "/pilot/purchase" });
        return;
      }

      history.replace("/pilot/purchase");
      return;
    }

    getProducts();
  }, [getProducts, history]);

  const handleProductClick = (product) => {
    selectProductAction(product);
    history.push("/pilot/purchase/confirm");
  };

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <ShoppingCartIcon />
        <span>Purchase</span>
      </div>
      <div className={classes.content}>
        {isLoading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        ) : !products || products.length === 0 ? (
          <div className={classes.emptyContainer}>
            No packages available at the moment
          </div>
        ) : (
          <div className={classes.productsGrid}>
            {productGroups.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onClick={handleProductClick}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  products: state.purchase?.products || [],
  isLoading: state.purchase?.isLoading || false,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getProducts: getProductsRequest,
      selectProductAction: selectProduct,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(Purchase);
